// packages/engine/src/process-test-capture.ts
//
// P8 — scrubber-capture. Turn a stepping session (the interactive scrubber's
// accumulated answers, or an agent's headless equivalent) into a `test` block.
//
// The pipeline is: {@link CaptureSession} (UI-agnostic answers, keyed by the
// same emitter nodeIds the P1a choice bridge and the scrubber's `branchSelection`
// use) → {@link capturePins} (invert the bridge: positional selections back to
// condition-string / `as` / list / `fails` pins) → {@link enumerateTraces} (the
// SAME evaluator the runner uses) → a single canonical {@link Trace} →
// {@link snapshotAssertions} (the observed occurrences + terminator as `then`).
//
// Round-trip by construction: the `then` snapshot is derived from evaluating the
// captured pins, so `printTest → parse → runTests` re-evaluates to the same trace
// (the P8 gate: step → save → re-run → identical trace). {@link captureTest} is
// pure and headless — an agent gathering a process from a participant calls it
// directly, no scrubber UI.

import type { Diagnostic, Name } from "./types.js";
import type { ProcessModel } from "./process-model.js";
import type { WalkerContext } from "./process-walker-types.js";
import type { ResolvedModel } from "./resolver.js";
import type { TestAssertion, TestDecl, TestPin, TestPinValue, TestRef } from "./v010-ast.js";
import type { Trace, FailureSpec } from "./process-trace-producer.js";
import { buildChoiceBridge, enumerateTraces } from "./process-trace-enumerator.js";
import { walkerContextFor } from "./process-contexts.js";

// -----------------------------------------------------------------------------
// the capture session — the headless contract
// -----------------------------------------------------------------------------

/** One failing step answered during a stepping session. `ref` is the failing
 *  step's callee path (its `to`, e.g. `["Payments","charge"]`); `outcomes` is the
 *  ordered per-occurrence result list (a single `[{fail}]`, or a per-attempt
 *  `[{fail,"declined"},{ok}]` retry sequence). */
export interface FailureAnswer {
  readonly ref: readonly Name[];
  readonly outcomes: FailureSpec;
}

/** One delayed step answered during a stepping session. `after` = business
 *  delay, `takes` = system delay (mirrors the `after`/`takes` pin split). */
export interface DelayAnswer {
  readonly ref: readonly Name[];
  readonly axis: "after" | "takes";
  readonly duration: string;
}

/**
 * The UI-agnostic accumulated answers of one stepping session — the headless
 * capture contract, keyed by the emitter nodeIds the scrubber's `branchSelection`
 * (and the P1a choice bridge) already use. The scrubber populates it from live
 * answers; an agent populates it directly.
 */
export interface CaptureSession {
  /** if/try container nodeId → chosen arm label (a condition text, `else`,
   *  `try`, or a catch label — exactly the scrubber's `branchSelection`). */
  readonly branchSelection?: ReadonlyMap<string, string>;
  /** select container nodeId → the ordered case indices that ran. */
  readonly selectRuns?: ReadonlyMap<string, readonly number[]>;
  /** failing action nodeId → the injected outcome. */
  readonly failures?: ReadonlyMap<string, FailureAnswer>;
  /** delayed action nodeId → the injected timing. */
  readonly delays?: ReadonlyMap<string, DelayAnswer>;
}

/** The outcome of a capture: the assembled `test`, the single canonical trace it
 *  snapshots (null if the pins under-constrain / the process is untestable), the
 *  captured pins, and any diagnostics (an ambiguous capture warns, never throws). */
export interface CaptureResult {
  readonly test: TestDecl;
  readonly trace: Trace | null;
  readonly pins: readonly TestPin[];
  readonly diagnostics: readonly Diagnostic[];
}

// -----------------------------------------------------------------------------
// ref builders
// -----------------------------------------------------------------------------

const nameRef = (name: string): TestRef => ({ segments: [{ kind: "name", value: name }] });
const stringRef = (value: string): TestRef => ({ segments: [{ kind: "string", value }] });
const pathRef = (segments: readonly Name[]): TestRef => ({ segments: segments.map((value) => ({ kind: "name", value })) });

/** A condition text is an expression, not an identifier — pin it as a quoted
 *  string segment (`"amount > limit": no`), the form the parser round-trips. */
const condRef = (text: string): TestRef => stringRef(text);

// -----------------------------------------------------------------------------
// capturePins — invert the choice bridge
// -----------------------------------------------------------------------------

/**
 * Invert a {@link CaptureSession}'s positional selections into `when` pins,
 * reusing the P1a choice bridge (`byContainer`/`selects`). Each accumulated
 * answer becomes exactly the pin the enumerator resolves back to the same choice:
 * an `if` arm → `"<cond>": yes`, its `else` → a `no` on every condition; a `try`
 * catch → `"<label>": fails`; a `select` run-set → `<head>: [caseA, caseB]`; a
 * failing step → `<Callee.step>: fails` (or a per-attempt sequence); a delay →
 * `<Callee.step>: after|takes <dur>`. The `try` success arm needs no pin.
 */
export function capturePins(process: ProcessModel, ctx: WalkerContext, session: CaptureSession): TestPin[] {
  const bridge = buildChoiceBridge(process, ctx);
  const pins: TestPin[] = [];

  for (const [container, arm] of session.branchSelection ?? new Map()) {
    const arms = bridge.byContainer.get(container) ?? [];
    const chosen = arms.find((p) => (p.armLabel ?? "") === arm);
    if (!chosen) continue;
    if (chosen.kind === "if") {
      if (arm === "else") {
        // else ⇒ every condition arm of this container is false.
        for (const p of arms) if ((p.armLabel ?? "") !== "else") pins.push({ key: condRef(p.raw), value: answer("no") });
      } else {
        pins.push({ key: condRef(chosen.raw), value: answer("yes") });
      }
    } else {
      // try/guard: an unpinned guard enumerates BOTH arms, so the observed arm is
      // pinned explicitly — the success arm by a positive `"try": pass` (a quoted
      // key so the `try` keyword round-trips), a chosen catch by a `fails` on its
      // label (a failure on the guarded step routes there).
      if (arm === "try") pins.push({ key: stringRef("try"), value: answer("pass") });
      else pins.push({ key: labelRef(chosen.raw), value: { kind: "fail" } });
    }
  }

  for (const [container, indices] of session.selectRuns ?? new Map<string, readonly number[]>()) {
    const sel = bridge.selects.get(container);
    const head = sel?.head?.text?.trim();
    if (!sel || !head) continue;
    const items = indices
      .map((i) => sel.cases[i]?.label?.text?.trim())
      .filter((x): x is string => x !== undefined && x !== "");
    pins.push({ key: nameRef(head), value: { kind: "list", items } });
  }

  for (const fa of (session.failures ?? new Map<string, FailureAnswer>()).values()) {
    if (fa.ref.length === 0) continue;
    pins.push({ key: pathRef(fa.ref), value: failureValue(fa.outcomes) });
  }

  for (const da of (session.delays ?? new Map<string, DelayAnswer>()).values()) {
    if (da.ref.length === 0) continue;
    pins.push({ key: pathRef(da.ref), value: { kind: da.axis, duration: da.duration } });
  }

  return pins;
}

const answer = (word: string): TestPinValue => ({ kind: "answer", word });

/** A catch label is usually an identifier but may be a quoted phrase — quote it
 *  when it isn't a bare identifier so the pin key round-trips. */
const IDENT = /^[A-Za-z_][A-Za-z0-9_]*$/;
const labelRef = (label: string): TestRef => (IDENT.test(label) ? nameRef(label) : stringRef(label));

/** A single outcome collapses to its atom; a per-occurrence list becomes a comma
 *  sequence (`fails "declined", ok`). */
function failureValue(outcomes: FailureSpec): TestPinValue {
  const atom = (o: FailureSpec[number]): TestPinValue =>
    o.kind === "fail" ? (o.cause !== undefined ? { kind: "fail", cause: o.cause } : { kind: "fail" }) : { kind: "answer", word: "ok" };
  if (outcomes.length === 1) return atom(outcomes[0]!);
  return { kind: "sequence", steps: outcomes.map(atom) };
}

// -----------------------------------------------------------------------------
// snapshotAssertions — the observed trace as `then`
// -----------------------------------------------------------------------------

/**
 * Snapshot a resolved trace's observed occurrences (in first-appearance order,
 * with an exact count when a step ran more than once) plus its terminal outcome
 * as `then` assertions. The count basis is the SAME `trace.steps` projection
 * `evalTest` counts against, so the snapshot re-evaluates to a pass by
 * construction. Note steps are matched by their text; anonymous steps (no callee
 * path, no note) are skipped.
 */
export function snapshotAssertions(trace: Trace): TestAssertion[] {
  const out: TestAssertion[] = [];
  const order: string[] = [];
  const counts = new Map<string, number>();
  const refByKey = new Map<string, TestRef>();
  for (const s of trace.steps) {
    let key: string | undefined;
    let ref: TestRef | undefined;
    if (s.noteText !== undefined) {
      key = `note:${s.noteText}`;
      ref = stringRef(s.noteText);
    } else if (s.to && s.to.length > 0) {
      key = s.to.join(".");
      ref = pathRef(s.to);
    }
    if (key === undefined || ref === undefined) continue;
    if (!counts.has(key)) {
      order.push(key);
      refByKey.set(key, ref);
    }
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  for (const key of order) {
    const n = counts.get(key)!;
    const ref = refByKey.get(key)!;
    out.push(n > 1 ? { kind: "occurrence", ref, modal: "happens", count: { op: "=", n } } : { kind: "occurrence", ref });
  }
  if (trace.end) {
    out.push({
      kind: "terminator",
      which: trace.end.type,
      ...(trace.end.reason !== undefined ? { label: trace.end.reason } : {}),
      ...(trace.end.all ? { all: true } : {}),
    });
  }
  return out;
}

// -----------------------------------------------------------------------------
// captureTest — the headless entry point
// -----------------------------------------------------------------------------

/**
 * Capture a stepping session as a `test` declaration. Pure + headless: invert the
 * session to pins, enumerate the pin-constrained trace-set (the runner's own
 * evaluator), snapshot the single canonical trace as `then`, and assemble the
 * `test of <Process>`. A session that under-constrains the process (≠1 trace)
 * still returns a test — the pins captured so far plus a warning — never throws.
 */
export function captureTest(
  process: ProcessModel,
  model: ResolvedModel,
  session: CaptureSession,
  opts: { readonly name: string },
): CaptureResult {
  const ctx = walkerContextFor(model, process);
  const pins = capturePins(process, ctx, session);
  const diagnostics: Diagnostic[] = [];

  const target = process.name && process.name !== "" ? ([process.name] as readonly Name[]) : undefined;
  if (target === undefined) {
    diagnostics.push({ severity: "error", message: "Process under capture has no name — cannot target a test.", code: "test.capture-untestable" });
    return { test: { name: opts.name, fields: [], pins, assertions: [] }, trace: null, pins, diagnostics };
  }

  const enumerated = enumerateTraces(process, pins, ctx, { model });
  diagnostics.push(...enumerated.diagnostics);
  const trace = enumerated.traces[0] ?? null;
  if (enumerated.traces.length !== 1) {
    diagnostics.push({
      severity: enumerated.traces.length === 0 ? "error" : "warning",
      message: `Captured pins resolve to ${enumerated.traces.length} traces, not one — the session under-constrains the process.`,
      code: "test.capture-ambiguous",
    });
  }
  const assertions = trace ? snapshotAssertions(trace) : [];

  return {
    test: { name: opts.name, target, fields: [], pins, assertions },
    trace,
    pins,
    diagnostics,
  };
}

// -----------------------------------------------------------------------------
// printTest — serialize a captured test back to `.arch` text
// -----------------------------------------------------------------------------

/** Quote a string the way the lexer reads one (double-quoted, backslash-escaped). */
function quote(s: string): string {
  return `"${s.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

function printRef(ref: TestRef): string {
  const body = ref.segments.map((s) => (s.kind === "string" ? quote(s.value) : s.value)).join(".");
  return ref.caller ? `${ref.caller.join(".")} > ${body}` : body;
}

function printPinValue(v: TestPinValue): string {
  switch (v.kind) {
    case "answer": return v.word;
    case "fail": return v.cause !== undefined ? `fails ${quote(v.cause)}` : "fails";
    case "sequence": return v.steps.map(printPinValue).join(", ");
    case "after": return `after ${v.duration}`;
    case "takes": return `takes ${v.duration}`;
    case "count": return String(v.n);
    case "list": return `[${v.items.join(", ")}]`;
    case "cause": return quote(v.value);
  }
}

function printAssertion(a: TestAssertion): string {
  switch (a.kind) {
    case "occurrence": {
      const parts = [printRef(a.ref)];
      if (a.modal) parts.push(a.modal);
      if (a.count) parts.push(a.count.op === "=" ? String(a.count.n) : `${a.count.op} ${a.count.n}`);
      return parts.join(" ");
    }
    case "terminator": {
      const head = a.which;
      if (a.all) return `${head} all`;
      return a.label !== undefined ? `${head} ${quote(a.label)}` : head;
    }
    case "time": return `time ${a.axis} ${a.op} ${a.duration}`;
    case "parallelGroup": return `parallel {\n${a.assertions.map((x) => `        ${printAssertion(x)}`).join("\n")}\n    }`;
  }
}

/**
 * Serialize a {@link TestDecl} to a `.arch` `test` block — the text the WYSIWYG
 * model-write path inserts. Uses 4-space indentation (the corpus convention).
 */
export function printTest(test: TestDecl): string {
  const I = "    ";
  const lines: string[] = [`test ${quote(test.name)}${test.target ? ` of ${test.target.join(".")}` : ""} {`];
  if (test.description !== undefined) lines.push(`${I}${quote(test.description)}`);
  if (test.pins.length > 0) {
    lines.push(`${I}when {`);
    for (const p of test.pins) lines.push(`${I}${I}${printRef(p.key)}: ${printPinValue(p.value)}`);
    lines.push(`${I}}`);
  }
  if (test.assertions.length > 0) {
    lines.push(`${I}then {`);
    for (const a of test.assertions) lines.push(`${I}${I}${printAssertion(a)}`);
    lines.push(`${I}}`);
  }
  lines.push("}");
  return lines.join("\n");
}
